import { useMemo } from 'react';
import type { ChartType, ChartOptions, ChartComponentLike, Plugin } from 'chart.js';
import { Chart as ChartJS } from 'chart.js';

import { defaultPlugins, optionalPlugins } from './index.ts';

const optionalPluginsMap = optionalPlugins.reduce((acc, plugin) => {
  acc.set(plugin.id, plugin);
  return acc;
}, new Map<string, Plugin>());

function detectRequiredPlugins<TType extends ChartType = ChartType>(options: ChartOptions<TType>): ChartComponentLike[] {
  if (!options) return [];
  if ('plugins' in options && options.plugins !== undefined) {
    return Array.from(
      Object.entries(options.plugins).reduce((acc, [key, value]) => {
        if (value) {
          const plugin = optionalPluginsMap.get(key);
          if (plugin) acc.add(plugin);
        }
        return acc;
      }, new Set<Plugin>()),
    );
  }
  return [];
}

const registered = new Set();

/* Detect required plugins from options. */
export function useRequiredChartPlugins<TType extends ChartType = ChartType>(options?: ChartOptions<TType>): ChartComponentLike[] {
  const plugins = useMemo(() => {
    if (!options) return [];
    const requiredPlugins = detectRequiredPlugins(options)
      .concat(defaultPlugins)
      .filter((plugin) => !registered.has(plugin));

    ChartJS.register(...requiredPlugins);
    requiredPlugins.forEach((p) => registered.add(p));
    return requiredPlugins;
  }, [options]);

  return plugins;
}
